import {
	createFileRoute,
	useParams,
	useRouteContext,
	stripSearchParams,
	Navigate,
	redirect,
	Link,
} from "@tanstack/react-router";
import { graphql, useLazyLoadQuery, fetchQuery } from "react-relay";
import { Post } from "@/ui/post";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";
import type { postsQuery } from "./__generated__/postsQuery.graphql";
import { environment } from "@/relay/environment";
import type { postsHeadQuery } from "./__generated__/postsHeadQuery.graphql";
import { getImageUrl } from "@/utils/getImageUrl";
import type { postsLoaderQuery } from "./__generated__/postsLoaderQuery.graphql";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/icon";

export const defaultValues = {
	comment_order: "trending",
} as const;

export const Route = createFileRoute(
	"/_visibility/_topnav/comunidade/s/$space/posts/$post",
)({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			comment_order: z
				.enum(["trending", "newest", "oldest"])
				.default("trending"),
		}),
	),
	search: {
		middlewares: [stripSearchParams(defaultValues)],
	},
	loader: async ({ context, params }) => {
		const data = await fetchQuery<postsLoaderQuery>(
			environment,
			graphql`
			query postsLoaderQuery($club: String!, $slug: String!) {
       post(club: $club, slug: $slug) {
         id

				space {
				  visibility
					isMember
					slug
					name
					icon
       }
       }
   }
	`,
			{ club: context.club.slug, slug: params.post },
		)
			.toPromise()
			.catch(() => undefined);

		if (
			!data?.post ||
			(!context.club?.permissions?.length &&
				data?.post.space?.visibility !== "PUBLIC" &&
				!data?.post.space?.isMember)
		) {
			throw redirect({
				to: "/comunidade/s/$space",
				params: {
					space: params.space,
				},
			});
		}

		return { club: context.club };
	},
	head: async ({ params: { post }, loaderData }) => {
		if (!loaderData?.club) {
			return {};
		}

		const data = await fetchQuery<postsHeadQuery>(
			environment,
			graphql`
	    query postsHeadQuery($club: String!, $slug: String!) {
	        post(club: $club, slug: $slug) {
	          id

	          title

						coverContent {
							... on ImageFile {
								signedUrl
							}
						}
	        }
	    }
	  `,
			{ club: loaderData.club.slug!, slug: post },
		).toPromise();

		if (!data?.post) {
			return {};
		}

		const ogImageUrl = data.post.coverContent?.signedUrl
			? getImageUrl(data.post.coverContent?.signedUrl, {
					format: "png",
					width: 1200,
					height: 630,
					quality: 80,
					maxBytes: 600,
				})
			: undefined;

		return {
			meta: [
				{
					title: `${data.post.title} | ${loaderData.club.name}`,
				},
				...(data.post.coverContent?.signedUrl
					? [
							{
								property: "og:image",
								content: ogImageUrl,
							},
							{
								property: "og:image:secure_url",
								content: ogImageUrl,
							},
							{
								property: "twitter:image",
								content: ogImageUrl,
							},
						]
					: []),
			],
		};
	},
});

function RouteComponent() {
	const { club } = useRouteContext({ from: "__root__" });
	const { post } = useParams({ from: Route.id });
	const data = useLazyLoadQuery<postsQuery>(
		graphql`
	    query postsQuery($club: String!, $slug: String!) {
	        post(club: $club, slug: $slug) {
	          id

						space {
						  visibility
							isMember
							slug
							name
							icon
						}

	          ...postFragment @arguments(club: $club)
	        }
	    }
	  `,
		{ club: club.slug, slug: post },
	);

	if (
		(club.permissions.length || 0) <= 0 &&
		data.post.space?.visibility !== "PUBLIC" &&
		!data.post.space?.isMember &&
		data.post.space?.slug
	) {
		return (
			<Navigate
				to="/comunidade/s/$space"
				params={{ space: data.post.space?.slug }}
			/>
		);
	}

	return (
		<>
			{data.post.space ? (
				<div className="flex items-center justify-start max-md:h-[72px] h-[60px] md:min-h-[60px] md:max-h-[60px] border-0 border-b z-2 px-6 bg-background backdrop-blur-md text-foreground">
					<Link
						to="/comunidade/s/$space"
						params={{ space: data.post.space.slug }}
					>
						<Button variant="ghost">
							<Icon name="ChevronLeft" />

							<span>
								{data.post.space?.icon
									? `${data.post.space.icon} : ${data.post.space.name}`
									: data.post.space.name}
							</span>
						</Button>
					</Link>
				</div>
			) : null}

			<div className="w-full flex items-start justify-center p-6 max-md:p-0">
				<div className="max-w-[800px] relative max-md:max-w-full w-full flex flex-col gap-6 max-md:gap-2">
					<Post post={data.post} />
				</div>
			</div>
		</>
	);
}
